import './index.css'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import RadioButton from 'components/Common/RadioButton'
import FormInput from 'components/Common/FormInput'
import Button from 'components/Common/Button'
import { PRIMARY_BUTTON, DISABLED_BUTTON, SECONDARY_BUTTON } from 'themes/theme'

import VaultBOTable from './VaultBOTable'
import { Dialog } from '@progress/kendo-react-dialogs'
import actionTypes from 'store/actions'

import { VaultAPI } from 'api'
import { stringUtils } from 'utils'
import { Typography, Box } from '@mui/material'
import Divider from '@mui/material/Divider';

const defaultBusinessData = {
  ownerType: 'B',
  boBzName: '',
  boEmail: '',
}

const defaultIndividualData = {
  ownerType: 'I',
  boFirstName: '',
  boLastName: '',
  boEmail: '',
}

const VaultBOAddPopup = props => {
  const dispatch = useDispatch()
  const vaultInfo = useSelector(state => state.vaultReducer.vaultSummary)
  // const vaultInfo = useSelector(state => state.apiResponseReducers.dashboardInfo.vault_info)
  const draftBOs = useSelector(state => state.vaultReducer.draftBOs)
  const [selectedOwnerType, setSelectedOwnerType] = useState('I')

  const [boInfo, setBoInfo] = useState({ ...defaultIndividualData })

  const onClickOwnerType = type => {
    type === 'I' ? setBoInfo(defaultIndividualData) : setBoInfo(defaultBusinessData)
    setSelectedOwnerType(type)
  }

  const isFormValid = () => {
    if (boInfo.ownerType === 'I') {
      return boInfo.boFirstName.length && boInfo.boLastName.length && stringUtils.emailValidator(boInfo.boEmail)
    } else {
      return boInfo.boBzName.length && stringUtils.emailValidator(boInfo.boEmail)
    }
  }

  const onAddBO = () => {
    const newDraftBO = {
      ownerType: boInfo.ownerType,
      displayName: boInfo.boBzName ? boInfo.boBzName : boInfo.boFirstName + ' ' + boInfo.boLastName,
      name: boInfo.boBzName ? boInfo.boBzName : boInfo.boFirstName + ' ' + boInfo.boLastName, //TODO: Just for demo
      firstName: boInfo.boFirstName,
      lastName: boInfo.boLastName,
      bzName: boInfo.boBzName,
      email: boInfo.boEmail,
      emailSent: new Date().toString(), //TODO: Get rid of this once API is ready.
      formCompleted: new Date().toString(), //TODO: Get rid of this once API is ready.
    }
    dispatch({ type: actionTypes.VAULTDRAFTBOADDED, payload: { BO: newDraftBO } })
    boInfo.ownerType === 'I' ? setBoInfo(defaultIndividualData) : setBoInfo(defaultBusinessData)
  }

  const onChangeBoInfo = (key, value) => {
    setBoInfo({ ...boInfo, [key]: value })
  }

  const onSubmitDraftBos = async () => {
    props.submitted(true)
    const draftBOResponse = await VaultAPI.submitDraftBOs(dispatch, vaultInfo.vault_id, vaultInfo.business_id, draftBOs)
    if (draftBOResponse?.beneficial_owner_id){
      for (let i=0; i< draftBOResponse?.beneficial_owner_id.length; i++ ){
        VaultAPI.submitBOHistory(draftBOResponse?.beneficial_owner_id[i], 'Email Sent')
      }
    }
  }

  return (
    <Dialog height="920px" width="900px">
      <Box textAlign="center" marginTop="40px" marginBottom="30px">
        <Typography variant="h5" fontWeight="600" fontsize="24px" color="#25245F">
          Add Beneficial Owners
        </Typography>
      </Box>
      <div className="vault-bo-add">
        <Box>
          <Typography variant="body2" color="#25245f">
            Beneficial Owners with 25% stake or more must be identified. Each beneficial owner will receive an email
            from you requesting they confirm their name, address, Social Security Number and percentage ownership in
            your business. You will receive a confirmation email when each beneficial owner completes the form.
          </Typography>
        </Box>

        <Divider sx={{ margin: '50px 0px' }} variant="middle" />

        <Box>
          <Typography variant="h6" color="#0A78CD">
            Add a Beneficial Owner
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center',
              margin: '10px 0px',
              color: '#25245F',
            }}
          >
            <RadioButton
              id="vault_bod_individual"
              value="I"
              label="Individual"
              selectedVaule={selectedOwnerType}
              onClick={() => onClickOwnerType('I')}
            />
            <RadioButton
              id="vault_bod_business"
              value="B"
              label="Business"
              selectedVaule={selectedOwnerType}
              onClick={() => onClickOwnerType('B')}
            />
          </Box>
        </Box>
        {selectedOwnerType === 'I' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center',
              color: '#25245F',
            }}
          >
            <FormInput
              required
              label="First Name"
              id="boFirstName"
              showError={false}
              value={boInfo.boFirstName}
              onChange={onChangeBoInfo}
              validator={value => value && value.length}
            />
            <FormInput
              label="Last Name"
              required
              id="boLastName"
              showError={false}
              value={boInfo.boLastName}
              onChange={onChangeBoInfo}
              validator={value => value && value.length}
            />
            <FormInput
              label="Email Address"
              required
              id="boEmail"
              showError={false}
              value={boInfo.boEmail}
              onChange={onChangeBoInfo}
              errorLabel="Wrong email address"
              validator={stringUtils.emailValidator}
              style={{ minWidth: '240px' }}
            />
            <Button
              style={{
                alignSelf: 'flex-end',
                marginBottom: '11px',
              }}
              type={isFormValid() ? PRIMARY_BUTTON : DISABLED_BUTTON}
              onClick={onAddBO}
            >
              Add
            </Button>
          </Box>
        )}

        {selectedOwnerType === 'B' && (
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center' }}>
            <FormInput
              label="Beneficial Owner Business Name"
              required
              id="boBzName"
              showError={false}
              value={boInfo.boBzName}
              onChange={onChangeBoInfo}
              validator={value => value && value.length}
            />
            <FormInput
              label="Email Address"
              required
              id="boEmail"
              showError={false}
              value={boInfo.boEmail}
              onChange={onChangeBoInfo}
              errorLabel="Wrong email address"
              validator={stringUtils.emailValidator}
              style={{ minWidth: '240px' }}
            />
            <Button
              style={{
                alignSelf: 'flex-end',
                marginBottom: '11px',
              }}
              onClick={onAddBO}
              type={isFormValid() ? PRIMARY_BUTTON : DISABLED_BUTTON}
            >
              Add
            </Button>
          </Box>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', marginTop: '40px', justifyContent: 'flex-start' }}>
          <Typography variant="h6" color="#0A78CD">
            Manage Beneficial Owners
          </Typography>
          <VaultBOTable />
        </Box>
        <Divider variant="middle" sx={{ marginY: '30px' }} />
        <Box display="flex" flexDirection="row" justifyContent="center" gap="15px" marginY="25px">
          <Button type={SECONDARY_BUTTON} sx={{ width: '200px' }} onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            type={!draftBOs.length ? DISABLED_BUTTON : PRIMARY_BUTTON}
            sx={{ width: '200px' }}
            onClick={onSubmitDraftBos}
          >
            Submit
          </Button>
        </Box>
      </div>
    </Dialog>
  )
}

export default VaultBOAddPopup
